import { useEffect, useRef, useCallback } from 'react'
import { usePlayer } from '@/hooks/usePlayer'

// ─── Constants ────────────────────────────────────────────────────────────────
const W = 400
const H = 520
const GRAVITY = 0.38
const JUMP = -13.5
const PLAT_H = 8
const PARROT_W = 36
const PARROT_H = 30
const PINK = '#ff2680'
const CYAN = '#00cfff'
const GOLD = '#ffd700'
const BG = '#07000a'
const MONO = "'JetBrains Mono','Courier New',monospace"

// ─── Types ────────────────────────────────────────────────────────────────────
type GameState = 'menu' | 'play' | 'over'

interface Platform {
  x: number
  y: number
  w: number
  mv: boolean
  dir: number
  sp: number
  boost: boolean
  bAnim: number
  hit: boolean
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  col: string
  size: number
  decay: number
}

interface Star {
  x: number
  y: number
  r: number
  b: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function makePlatform(y: number, first = false): Platform {
  const w = first ? 100 : 50 + Math.random() * 70
  const x = first ? W / 2 - 50 : Math.random() * (W - w)
  const mv = !first && Math.random() < 0.28
  const boost = !first && !mv && Math.random() < 0.12
  return {
    x, y, w, mv,
    dir: Math.random() < 0.5 ? 1 : -1,
    sp: 1.2 + Math.random() * 1.8,
    boost, bAnim: 0, hit: false,
  }
}

function genStars(): Star[] {
  return Array.from({ length: 60 }, () => ({
    x: Math.random() * W,
    y: Math.random() * H,
    r: Math.random() * 1.5 + 0.3,
    b: Math.random(),
  }))
}

// ─── Drawing helpers ──────────────────────────────────────────────────────────
function drawParrot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  facingLeft: boolean,
  wingT: number,
  squash: number,
) {
  ctx.save()
  ctx.translate(x + PARROT_W / 2, y + PARROT_H / 2)
  if (facingLeft) ctx.scale(-1, 1)
  ctx.scale(1, squash)

  ctx.fillStyle = '#0099cc'
  ctx.beginPath()
  ctx.ellipse(-18, 8, 10, 5, Math.PI * 0.3, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.ellipse(-20, 3, 8, 4, Math.PI * 0.15, 0, Math.PI * 2)
  ctx.fill()

  const wa = Math.sin(wingT) * 0.45
  ctx.save()
  ctx.rotate(wa - 0.1)
  ctx.fillStyle = '#007aaa'
  ctx.beginPath()
  ctx.ellipse(0, 5, 14, 8, 0.2, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  ctx.fillStyle = CYAN
  ctx.beginPath()
  ctx.ellipse(0, 0, 16, 14, 0, 0, Math.PI * 2)
  ctx.fill()

  ctx.save()
  ctx.rotate(wa)
  ctx.fillStyle = '#33ddff'
  ctx.beginPath()
  ctx.ellipse(0, 4, 11, 6, 0.15, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.beginPath()
  ctx.ellipse(2, 3, 8, 8, 0, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = '#22eeff'
  ctx.beginPath()
  ctx.arc(12, -10, 10, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = PINK
  ctx.beginPath()
  ctx.moveTo(14, -20); ctx.lineTo(18, -16); ctx.lineTo(10, -16)
  ctx.fill()
  ctx.beginPath()
  ctx.moveTo(10, -19); ctx.lineTo(14, -15); ctx.lineTo(7, -15)
  ctx.fill()

  ctx.fillStyle = '#fff'
  ctx.beginPath()
  ctx.arc(16, -12, 4, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#07000a'
  ctx.beginPath()
  ctx.arc(17, -12, 2.2, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#fff'
  ctx.beginPath()
  ctx.arc(17.5, -12.5, 0.8, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = GOLD
  ctx.beginPath()
  ctx.moveTo(22, -10); ctx.lineTo(28, -8); ctx.lineTo(22, -6)
  ctx.fill()
  ctx.fillStyle = '#e6a800'
  ctx.beginPath()
  ctx.moveTo(22, -9); ctx.lineTo(27, -8); ctx.lineTo(22, -8)
  ctx.fill()

  ctx.restore()
}

function drawPlatform(
  ctx: CanvasRenderingContext2D,
  p: Platform,
  screenY: number,
  tick: number,
) {
  const col = p.boost ? GOLD : p.mv ? PINK : CYAN
  ctx.shadowColor = col
  ctx.shadowBlur = 12
  ctx.fillStyle = col
  ctx.beginPath()
  ctx.roundRect(p.x, screenY, p.w, PLAT_H, 4)
  ctx.fill()
  ctx.shadowBlur = 0

  const grd = ctx.createLinearGradient(p.x, screenY, p.x, screenY + PLAT_H)
  grd.addColorStop(0, 'rgba(255,255,255,0.45)')
  grd.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = grd
  ctx.beginPath()
  ctx.roundRect(p.x, screenY, p.w, 3, 3)
  ctx.fill()

  if (p.boost) {
    ctx.fillStyle = 'rgba(255,215,0,0.25)'
    ctx.beginPath()
    ctx.roundRect(p.x, screenY - 9, p.w, 6, 3)
    ctx.fill()
    const bob = Math.sin(tick * 0.12) * 2
    ctx.fillStyle = GOLD
    ctx.font = `bold 9px ${MONO}`
    ctx.textAlign = 'center'
    ctx.fillText('↑ BOOST', p.x + p.w / 2, screenY - 13 + bob)
  }

  if (p.mv) {
    ctx.fillStyle = 'rgba(255,38,128,0.2)'
    ctx.beginPath()
    ctx.roundRect(p.x - 3, screenY + PLAT_H, p.w + 6, 2, 1)
    ctx.fill()
  }
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ParrotJump() {
  const { submitScore } = usePlayer()          // ADD
  const submitScoreRef = useRef(submitScore)   // ADD — keeps loop closure fresh
  useEffect(() => { submitScoreRef.current = submitScore }, [submitScore])  // ADD

  const canvasRef = useRef<HTMLCanvasElement>(null)

  const gs = useRef({
    state: 'menu' as GameState,
    tick: 0,
    score: 0,
    best: 0,
    parrotX: W / 2 - PARROT_W / 2,
    parrotY: H - 120,
    pvx: 0,
    pvy: 0,
    facingLeft: false,
    cam: 0,
    highY: H - 120,
    combo: 0,
    comboTimer: 0,
    lastLandId: -1,
    wingT: 0,
    squashT: 0,
    plats: [] as Platform[],
    particles: [] as Particle[],
    stars: genStars(),
    keys: {} as Record<string, boolean>,
    platCounter: 0,
    submitted: false,  // ADD — prevents double-submit per run
  })

  const rafRef = useRef<number>(0)

  const initGame = useCallback(() => {
    const g = gs.current
    g.plats = []
    g.particles = []
    g.parrotX = W / 2 - PARROT_W / 2
    g.parrotY = H - 120
    g.pvx = 0
    g.pvy = 0
    g.facingLeft = false
    g.cam = 0
    g.highY = H - 120
    g.score = 0
    g.combo = 0
    g.comboTimer = 0
    g.lastLandId = -1
    g.wingT = 0
    g.squashT = 0
    g.tick = 0
    g.platCounter = 0
    g.submitted = false  // ADD

    g.plats.push({ ...makePlatform(H - 90, true), hit: false })
    for (let i = 1; i < 10; i++) {
      g.plats.push(makePlatform(H - 90 - i * 72, false))
    }
    g.state = 'play'
  }, [])

  const addParticles = useCallback(
    (x: number, y: number, col: string, n: number, big: boolean) => {
      const g = gs.current
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2
        const sp = big ? 3 + Math.random() * 4 : 1.5 + Math.random() * 3
        g.particles.push({
          x, y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - (big ? 2.5 : 0),
          life: 1,
          col,
          size: big ? 3.5 : 2.5,
          decay: 0.022 + Math.random() * 0.025,
        })
      }
    },
    [],
  )

  const loop = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) { rafRef.current = requestAnimationFrame(loop); return }
    const ctx = canvas.getContext('2d')!
    const g = gs.current

    g.tick++
    g.wingT += 0.22

    if (g.state === 'play') {
      g.comboTimer = Math.max(0, g.comboTimer - 1)
      if (g.comboTimer === 0) g.combo = 0

      if (g.keys['ArrowLeft'] || g.keys['KeyA']) {
        g.pvx = Math.max(g.pvx - 1.1, -8)
        g.facingLeft = true
      } else if (g.keys['ArrowRight'] || g.keys['KeyD']) {
        g.pvx = Math.min(g.pvx + 1.1, 8)
        g.facingLeft = false
      } else {
        g.pvx *= 0.82
        if (Math.abs(g.pvx) < 0.1) g.pvx = 0
      }

      if ((g.keys['Space'] || g.keys['ArrowUp']) && g.pvy > 0) {
        g.pvy = Math.max(g.pvy - 1.8, -3)
      }

      g.pvy = Math.min(g.pvy + GRAVITY, 16)
      g.parrotX += g.pvx
      g.parrotY += g.pvy

      if (g.parrotX + PARROT_W < 0) g.parrotX = W
      if (g.parrotX > W) g.parrotX = -PARROT_W

      if (g.pvy > 0) {
        for (let i = 0; i < g.plats.length; i++) {
          const p = g.plats[i]
          const sy = p.y - g.cam
          const parBottom = g.parrotY + PARROT_H
          const parLeft = g.parrotX + 4
          const parRight = g.parrotX + PARROT_W - 4

          if (
            parRight > p.x &&
            parLeft < p.x + p.w &&
            parBottom >= sy &&
            parBottom <= sy + PLAT_H + g.pvy + 2
          ) {
            const isNew = i !== g.lastLandId
            if (isNew) {
              g.combo++
              g.comboTimer = 90
              g.lastLandId = i
              const bonus = p.boost ? 20 : p.mv ? 15 : 10 + (g.combo > 1 ? (g.combo - 1) * 5 : 0)
              g.score += bonus
              if (g.combo > 1) {
                addParticles(g.parrotX + PARROT_W / 2, parBottom - g.cam, g.combo > 3 ? GOLD : PINK, g.combo > 3 ? 14 : 8, true)
              }
            }

            g.pvy = p.boost ? JUMP * 1.5 : JUMP
            g.parrotY = sy - PARROT_H
            g.squashT = 0

            if (p.boost) {
              addParticles(g.parrotX + PARROT_W / 2, g.parrotY + PARROT_H, GOLD, 20, true)
              p.bAnim = 15
            }
            addParticles(g.parrotX + PARROT_W / 2, g.parrotY + PARROT_H, p.boost ? GOLD : p.mv ? PINK : CYAN, 8, false)
            p.hit = true
            break
          }
        }
      }

      const screenY = g.parrotY - g.cam
      if (screenY < H * 0.38) {
        g.cam -= H * 0.38 - screenY
      }

      if (g.parrotY < g.highY) {
        g.score += Math.floor((g.highY - g.parrotY) / 8)
        g.highY = g.parrotY
      }

      for (const p of g.plats) {
        if (p.mv) {
          p.x += p.dir * p.sp
          if (p.x < 0 || p.x + p.w > W) p.dir *= -1
        }
        if (p.bAnim > 0) p.bAnim--
      }

      g.plats = g.plats.filter(p => p.y < g.cam + H + 200)

      while (g.plats.length < 12) {
        const minY = g.plats.reduce((m, p) => Math.min(m, p.y), Infinity)
        const newY = (isFinite(minY) ? minY : g.cam) - 65 - Math.random() * 30
        g.plats.push(makePlatform(newY, false))
      }

      for (const p of g.particles) {
        p.x += p.vx
        p.y += p.vy
        p.vy += 0.08
        p.life -= p.decay
      }
      g.particles = g.particles.filter(p => p.life > 0)

      g.squashT = Math.min(g.squashT + 1, 20)

      // Death — ADD submitted guard to prevent double-submit
      if (g.parrotY - g.cam > H + 80 && !g.submitted) {
        g.submitted = true          // ADD
        g.state = 'over'
        if (g.score > g.best) g.best = g.score
        submitScoreRef.current('parrot-jump', g.score)  // ADD
      }
    }

    // ── Draw ──────────────────────────────────────────────────────────────────
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = BG
    ctx.fillRect(0, 0, W, H)

    for (const s of g.stars) {
      const blink = 0.25 + 0.6 * Math.abs(Math.sin(g.tick * 0.01 + s.b * 10))
      ctx.fillStyle = `rgba(0,207,255,${blink * 0.4})`
      ctx.beginPath()
      ctx.arc(s.x, ((s.y - g.cam * 0.07) % H + H) % H, s.r, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.strokeStyle = 'rgba(0,207,255,0.022)'
    ctx.lineWidth = 1
    const gOff = (g.cam * 0.3) % 40
    for (let x = 0; x < W; x += 40) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
    }
    for (let y = -gOff; y < H; y += 40) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
    }

    for (const p of g.plats) {
      const sy = p.y - g.cam
      if (sy < -20 || sy > H + 20) continue
      drawPlatform(ctx, p, sy, g.tick)
    }

    for (const p of g.particles) {
      ctx.globalAlpha = Math.max(0, p.life)
      ctx.fillStyle = p.col
      ctx.shadowColor = p.col
      ctx.shadowBlur = 5
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.globalAlpha = 1
    ctx.shadowBlur = 0

    if (g.state === 'play' || g.state === 'over') {
      const squash = g.squashT < 5
        ? 0.72 + g.squashT * 0.056
        : Math.min(1, 0.72 + (20 - g.squashT) * 0.014 + 0.5)
      drawParrot(
        ctx,
        g.parrotX,
        g.parrotY - g.cam,
        g.facingLeft,
        g.wingT,
        Math.max(0.72, Math.min(1.12, squash)),
      )
    }

    ctx.fillStyle = CYAN
    ctx.font = `bold 12px ${MONO}`
    ctx.textAlign = 'left'
    ctx.shadowColor = CYAN
    ctx.shadowBlur = 5
    ctx.fillText(`SCORE  ${g.score}`, 10, 22)
    ctx.textAlign = 'right'
    ctx.fillText(`BEST  ${g.best}`, W - 10, 22)
    ctx.shadowBlur = 0

    if (g.combo > 1 && g.comboTimer > 0) {
      const alpha = Math.min(1, g.comboTimer / 30)
      ctx.globalAlpha = alpha
      ctx.fillStyle = g.combo > 3 ? GOLD : PINK
      ctx.font = `bold ${11 + Math.min(g.combo, 8)}px ${MONO}`
      ctx.textAlign = 'center'
      ctx.shadowColor = g.combo > 3 ? GOLD : PINK
      ctx.shadowBlur = 10
      ctx.fillText(`×${g.combo} COMBO!`, W / 2, 50)
      ctx.shadowBlur = 0
      ctx.globalAlpha = 1
    }

    if (g.state === 'menu') {
      ctx.fillStyle = 'rgba(7,0,10,0.78)'
      ctx.fillRect(0, 0, W, H)

      ctx.textAlign = 'center'
      ctx.fillStyle = CYAN
      ctx.font = `900 30px 'Arial Black',sans-serif`
      ctx.shadowColor = CYAN
      ctx.shadowBlur = 22
      ctx.fillText('PARROT JUMP', W / 2, H / 2 - 80)
      ctx.shadowBlur = 0

      ctx.fillStyle = 'rgba(0,207,255,0.55)'
      ctx.font = `11px ${MONO}`
      ctx.fillText('← → / A D  move', W / 2, H / 2 - 30)
      ctx.fillText('SPACE / ↑  flap upward', W / 2, H / 2 - 12)

      ctx.fillStyle = PINK
      ctx.font = `10px ${MONO}`
      ctx.fillText('pink = moving   gold = boost ×1.5', W / 2, H / 2 + 10)

      const blink = Math.sin(g.tick * 0.08) > 0
      if (blink) {
        ctx.fillStyle = CYAN
        ctx.font = `bold 12px ${MONO}`
        ctx.shadowColor = CYAN
        ctx.shadowBlur = 8
        ctx.fillText('PRESS SPACE TO START', W / 2, H / 2 + 50)
        ctx.shadowBlur = 0
      }
      drawParrot(ctx, W / 2 - PARROT_W / 2, H / 2 + 85, false, g.wingT, 1)
    }

    if (g.state === 'over') {
      ctx.fillStyle = 'rgba(7,0,10,0.88)'
      ctx.fillRect(0, 0, W, H)

      ctx.textAlign = 'center'
      ctx.fillStyle = PINK
      ctx.font = `900 42px 'Arial Black',sans-serif`
      ctx.shadowColor = PINK
      ctx.shadowBlur = 28
      ctx.fillText('SPLAT', W / 2, H / 2 - 72)
      ctx.shadowBlur = 0

      ctx.fillStyle = 'rgba(255,255,255,0.3)'
      ctx.font = `11px ${MONO}`
      ctx.fillText('GAME OVER', W / 2, H / 2 - 42)

      ctx.fillStyle = CYAN
      ctx.font = `bold 30px ${MONO}`
      ctx.shadowColor = CYAN
      ctx.shadowBlur = 12
      ctx.fillText(`${g.score} PTS`, W / 2, H / 2 - 8)
      ctx.shadowBlur = 0

      if (g.score > 0 && g.score >= g.best) {
        ctx.fillStyle = GOLD
        ctx.font = `bold 11px ${MONO}`
        ctx.shadowColor = GOLD
        ctx.shadowBlur = 8
        ctx.fillText('★ NEW BEST ★', W / 2, H / 2 + 18)
        ctx.shadowBlur = 0
      }

      ctx.fillStyle = 'rgba(0,207,255,0.45)'
      ctx.font = `10px ${MONO}`
      ctx.fillText(`BEST: ${g.best}`, W / 2, H / 2 + 36)

      const blink = Math.sin(g.tick * 0.09) > 0
      if (blink) {
        ctx.fillStyle = CYAN
        ctx.font = `bold 12px ${MONO}`
        ctx.shadowColor = CYAN
        ctx.shadowBlur = 6
        ctx.fillText('PRESS SPACE TO RETRY', W / 2, H / 2 + 65)
        ctx.shadowBlur = 0
      }
    }

    rafRef.current = requestAnimationFrame(loop)
  }, [addParticles])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const g = gs.current
      g.keys[e.code] = true

      if (['Space', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.code)) {
        e.preventDefault()
      }

      if (e.code === 'Space') {
        if (g.state === 'menu' || g.state === 'over') {
          initGame()
        }
      }
    }

    const onKeyUp = (e: KeyboardEvent) => {
      gs.current.keys[e.code] = false
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const canvas = canvasRef.current
    let touchStartX = 0

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault()
      const g = gs.current
      touchStartX = e.touches[0].clientX
      if (g.state === 'menu' || g.state === 'over') {
        initGame()
        return
      }
      g.keys['Space'] = true
      setTimeout(() => { gs.current.keys['Space'] = false }, 80)
    }

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      const dx = e.touches[0].clientX - touchStartX
      const g = gs.current
      g.keys['ArrowLeft'] = dx < -15
      g.keys['ArrowRight'] = dx > 15
    }

    const onTouchEnd = (e: TouchEvent) => {
      e.preventDefault()
      const g = gs.current
      g.keys['ArrowLeft'] = false
      g.keys['ArrowRight'] = false
    }

    canvas?.addEventListener('touchstart', onTouchStart, { passive: false })
    canvas?.addEventListener('touchmove', onTouchMove, { passive: false })
    canvas?.addEventListener('touchend', onTouchEnd, { passive: false })

    rafRef.current = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      canvas?.removeEventListener('touchstart', onTouchStart)
      canvas?.removeEventListener('touchmove', onTouchMove)
      canvas?.removeEventListener('touchend', onTouchEnd)
    }
  }, [loop, initGame])

  return (
    <div
      style={{
        minHeight: '100vh',
        background: BG,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: MONO,
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 10,
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.06) 2px, rgba(0,0,0,0.06) 4px)',
        }}
      />

      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        style={{
          display: 'block',
          border: '1px solid rgba(0,207,255,0.18)',
          boxShadow: '0 0 60px rgba(0,207,255,0.12)',
          borderRadius: 8,
          touchAction: 'none',
          cursor: 'none',
        }}
      />

      <div
        style={{
          marginTop: 10,
          fontSize: 9,
          color: 'rgba(255,255,255,0.18)',
          letterSpacing: 2,
        }}
      >
        ← → MOVE &nbsp;|&nbsp; SPACE / ↑ FLAP &nbsp;|&nbsp; GOLD = BOOST
      </div>
    </div>
  )
}